import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { addGuestbook, listGuestbook } from '@/lib/weddingApi';
import type { GuestbookEntry } from '@/types/wedding';

interface Props {
  invitationId: string;
  interactive: boolean;
}

function formatDateTime(value: string): string {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

export default function GuestbookBlock({ invitationId, interactive }: Props) {
  const [entries, setEntries] = useState<GuestbookEntry[]>([]);
  const [loading, setLoading] = useState(interactive);
  const [loadError, setLoadError] = useState('');
  const [name, setName] = useState('');
  const [side, setSide] = useState('신랑');
  const [message, setMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [formError, setFormError] = useState('');
  const [posted, setPosted] = useState(false);

  const load = useCallback(async () => {
    if (!interactive || !invitationId) return;
    setLoading(true);
    setLoadError('');
    try {
      setEntries(await listGuestbook(invitationId));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : '방명록을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [interactive, invitationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!interactive) return;
    const trimmedName = name.trim();
    const trimmedMsg = message.trim();
    if (!trimmedName || !trimmedMsg) {
      setFormError('이름과 축하 메시지를 모두 입력해 주세요.');
      return;
    }
    setPosting(true);
    setFormError('');
    try {
      const created = await addGuestbook({
        invitationId,
        name: trimmedName,
        side,
        message: trimmedMsg,
      });
      setEntries((prev) => [created, ...prev]);
      setName('');
      setMessage('');
      setPosted(true);
      window.setTimeout(() => setPosted(false), 2600);
    } catch (err) {
      setFormError(
        err instanceof Error
          ? `등록에 실패했어요. ${err.message}`
          : '등록에 실패했어요. 잠시 후 다시 시도해 주세요.',
      );
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="inv-sec" id="guestbook">
      <p className="inv-kicker">Guest Book</p>
      <h3 className="inv-sec-title">축하의 마음을 남겨 주세요</h3>
      <p className="inv-sec-sub">
        남겨 주신 글은 두 사람에게 오래도록 남는 선물이 됩니다.
      </p>

      <form className="inv-form gb-form" onSubmit={submit}>
        <div className="inv-field-row">
          <div className="inv-field">
            <label htmlFor="gb-name">이름</label>
            <input
              id="gb-name"
              className="inv-input"
              value={name}
              disabled={!interactive}
              maxLength={20}
              onChange={(e) => setName(e.target.value)}
              placeholder="성함"
            />
          </div>
          <div className="inv-field">
            <label>구분</label>
            <div className="inv-choice">
              {['신랑', '신부'].map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={!interactive}
                  className={`inv-choice-btn ${side === v ? 'is-active' : ''}`}
                  onClick={() => setSide(v)}
                >
                  {v} 측
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="inv-field">
          <label htmlFor="gb-message">축하 메시지</label>
          <textarea
            id="gb-message"
            className="inv-input inv-textarea"
            value={message}
            disabled={!interactive}
            maxLength={300}
            onChange={(e) => setMessage(e.target.value.slice(0, 300))}
            placeholder="따뜻한 축하의 한마디를 남겨 주세요"
          />
          <span className="inv-count">{message.length} / 300</span>
        </div>

        {formError ? (
          <p className="inv-note err">
            <i className="ri-error-warning-line" aria-hidden="true" />
            {formError}
          </p>
        ) : null}

        <button type="submit" className="inv-submit" disabled={!interactive || posting}>
          {posting ? '남기는 중…' : '방명록 남기기'}
          <i className="ri-quill-pen-line" aria-hidden="true" />
        </button>
        {posted ? <p className="inv-form-hint ok">방명록에 남겼어요. 감사합니다!</p> : null}
        {!interactive ? (
          <p className="inv-form-hint">미리보기 화면이에요. 실제 청첩장에서 남길 수 있어요.</p>
        ) : null}
      </form>

      <div className="gb-list">
        {loading ? (
          <p className="inv-form-hint">방명록을 불러오는 중…</p>
        ) : loadError ? (
          <div className="inv-note err">
            <i className="ri-error-warning-line" aria-hidden="true" />
            <span>{loadError}</span>
            <button type="button" className="inv-retry" onClick={() => void load()}>
              다시 시도
            </button>
          </div>
        ) : entries.length === 0 ? (
          <p className="inv-form-hint">아직 남겨진 글이 없어요. 첫 번째 축하를 남겨 보세요.</p>
        ) : (
          entries.map((entry) => (
            <article className="gb-item" key={entry.id}>
              <div className="gb-item-head">
                <span className="gb-name">{entry.name}</span>
                <span className="gb-side">{entry.side} 측</span>
              </div>
              <p className="gb-msg">{entry.message}</p>
              <p className="gb-date">{formatDateTime(entry.created_at)}</p>
            </article>
          ))
        )}
      </div>
    </section>
  );
}